import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { FileInput } from '../../../../components/file-input/FileInput';
import ImageInput from '../../../../components/image-input/ImageInput';
import { ApiResultModal } from '../../../../components/modal/ApiResultHandler';
import { SearchAndSelect } from '../../../../components/search-input/DetailedSearchBar';
import BasicDatePicker from '../../../../components/select/DatePicker';
import {
  DepartmentSelect,
  TypeSelect,
} from '../../../../components/select/SelectList';
import {
  PrimaryButton,
  PrimaryOutlinedButton,
} from '../../../../components/style/ButtonStyle';
import { FormDivider } from '../../../../components/style/CommonStyle';
import { InfoText } from '../../../../components/style/TabStyle';
import {
  FormLabelText,
  FormTitle,
  MobileTitle,
} from '../../../../components/style/TextStyle';
import { useAuthAxios } from '../../../../hooks/useAuthAxios';
import { useMapSearchParams, useNaverMap } from '../../../../hooks/useMap';
import useResponsive from '../../../../hooks/useResponsive';
import { useYupValidationResolver } from '../../../../hooks/useYupValidationResolver';
import { MoreInformation } from '../../../../layouts/dashboard/nav/IconButton';
import {
  CategoryTypeList,
  OUTDOOR_GYM_EQUIPMENT_ID,
  OwnershipTypeList,
  responsiveMargin,
} from '../../../../utils/enum';
import { fDate } from '../../../../utils/formatTime';
import markerImg from '../../../static/images/select-marker.png';
import { ScrollUpButton } from '../CommonComponent/ScrollUpButton';
import AddOutdoorEquipmentButton from './AddOutdoorEquipmentButton';
import { TextAreaInput, TextInput, UnLabelTextInput } from './CommonForm';
import {
  facilityDefaultValues,
  facilitySchema,
} from './FacilitySchemaAndDefaultValues';

export default function EditFacilityFormPage() {
  const mdUp = useResponsive('up', 'md');
  const { facilityId } = useParams();

  const [getFacilityResult] = useAuthAxios(
    {
      method: 'GET',
      url: `/api/v1/facility/${facilityId}`,
    },
    { manual: false },
  );

  const facility = _.get(getFacilityResult, 'response.data.result');
  const resolver = useYupValidationResolver(
    Yup.object().shape({
      ...facilitySchema,
      installCostNation:
        Yup.mixed().isNumberErrorWhenDefined('숫자만 입력해주세요.'),
      installCostCity:
        Yup.mixed().isNumberErrorWhenDefined('숫자만 입력해주세요.'),
      installCostDistrict:
        Yup.mixed().isNumberErrorWhenDefined('숫자만 입력해주세요.'),
      occupyingSpace:
        Yup.mixed().isNumberErrorWhenDefined('숫자만 입력해주세요.'),
    }),
  );
  const { lat, lng, jibunAddress } = useMapSearchParams();
  const form = useForm<any>({
    mode: 'onTouched',
    resolver,
    defaultValues: {
      latitude: lat,
      longitude: lng,
      addressName: jibunAddress,
      ...facilityDefaultValues,
    },
    shouldUnregister: false,
  });

  const setValue = form.setValue;
  useEffect(() => {
    if (facility) {
      setValue('latitude', lat);
      setValue('longitude', lng);
      setValue('addressName', jibunAddress);
      setValue('addressNote', facility?.addressNote);
      setValue('categoryId', facility?.categoryId);
      setValue('categoryType', facility?.categoryType);
      setValue('title', facility?.title);
      setValue('serialNumber', facility?.serialNumber);
      setValue('dateOfInstall', new Date(facility?.dateOfInstall));
      setValue('maintenanceDepartment', facility?.maintenanceDepartmentId);
      setValue('subCategoryType', facility.subCategoryType);
      setValue('subCategoryId', facility?.subCategoryId);
      setValue('ownershipType', facility?.ownershipType);
      setValue(
        'usedAs',
        facility?.usedAs === null ? undefined : facility?.usedAs,
      );
      setValue('occupyingSpace', facility?.occupyingSpace);
      setValue('installCostNation', facility?.installCostNation);
      setValue('installCostCity', facility?.installCostCity);
      setValue('installCostDistrict', facility?.installCostDistrict);
      setValue('installDepartment', facility?.installDepartmentId);
      setValue('manageDepartment', facility?.manageDepartmentId);
      setValue('contactNumber', facility?.contactNumber);
      setValue('consignedOrg', facility?.consignedOrgId);
      setValue(
        'consignedStartDate',
        facility?.consignedStartDate === null
          ? null
          : new Date(facility?.consignedStartDate),
      );
      setValue(
        'consignedEndDate',
        facility?.consignedStartDate === null
          ? null
          : new Date(facility?.consignedEndDate),
      );
      setValue('administrativeRegion', facility?.administrativeRegion);
      setValue('actualRegion', facility?.actualRegion);
      setValue('notes', facility?.notes === null ? undefined : facility?.notes);
      setValue('facilityId', facility?.facilityId);
      setValue('overviewImageUrl', facility?.overviewImageUrl);
      setValue('facilityImageUrl', facility?.facilityImageUrl);
      setValue('tagImageUrl', facility?.tagImageUrl);
      setValue('attachmentDetails', facility?.attachmentDetails);
    }
  }, [facility, jibunAddress, lat, lng, setValue]);

  const [putFacilityResult, putFacility] = useAuthAxios({
    method: 'PUT',
    url: `/api/v1/facility/${facilityId}`,
  });

  const onSubmit = form.handleSubmit(function (values) {
    const data = new FormData();
    const serial = values.serialNumber;
    const serialWithGroupOfIndex = serial?.length - 2;
    let payload = {
      latitude: values.latitude,
      longitude: values.longitude,
      addressName: values.addressName,
      addressNote: values.addressNote,
      categoryType:
        values.categoryId === OUTDOOR_GYM_EQUIPMENT_ID
          ? CategoryTypeList.OUTDOOR_GYM_EQUIPMENT
          : CategoryTypeList.REGULAR,
      categoryId: values.categoryId,
      title: values.title,
      groupSerial: serial?.slice(0, serialWithGroupOfIndex),
      serialWithGroup: serial?.slice(serialWithGroupOfIndex),
      dateOfInstall: fDate(values.dateOfInstall, 'yyyy-MM-dd'),
      maintenanceDepartment:
        values.maintenanceDepartment === ''
          ? undefined
          : values.maintenanceDepartment === null
          ? undefined
          : values.maintenanceDepartment,
      subCategoryType:
        values.subCategoryType === '' ? undefined : values.subCategoryType,
      subCategoryId:
        values.subCategoryId === ''
          ? undefined
          : values.subCategoryId === null
          ? undefined
          : values.subCategoryId,
      ownershipType: values.ownershipType,
      usedAs: values.usedAs === '' ? undefined : values.usedAs,
      occupyingSpace:
        values.occupyingSpace === null ? undefined : values.occupyingSpace,
      installCostNation:
        values.installCostNation === null
          ? undefined
          : values.installCostNation,
      installCostCity:
        values.installCostCity === null ? undefined : values.installCostCity,
      installCostDistrict:
        values.installCostDistrict === null
          ? undefined
          : values.installCostDistrict,
      installDepartment:
        values.installDepartment === '' ? undefined : values.installDepartment,
      manageDepartment:
        values.manageDepartment === '' ? undefined : values.manageDepartment,
      contactNumber:
        values.contactNumber === ''
          ? undefined
          : values.contactNumber === null
          ? undefined
          : values.contactNumber,
      consignedOrg:
        values.consignedOrg === ''
          ? undefined
          : values.consignedOrg === null
          ? undefined
          : values.consignedOrg,
      consignedStartDate:
        values.consignedStartDate === null
          ? null
          : fDate(values.consignedStartDate, 'yyyy-MM-dd'),
      consignedEndDate:
        values.consignedEndDate === null
          ? null
          : fDate(values.consignedEndDate, 'yyyy-MM-dd'),
      administrativeRegion:
        values.administrativeRegion === ''
          ? undefined
          : values.administrativeRegion,
      actualRegion:
        values.actualRegion === '' ? undefined : values.actualRegion,
      notes: values.notes === '' ? undefined : values.notes,
      facilityId: values.facilityId,
      deletedAttachments: values.deletedAttachments,
    };
    let submit = _.pickBy(payload, value => typeof value !== 'undefined');
    _.keys(submit).forEach(k => {
      data.append(k, submit[k]);
    });
    data.append('overviewImage', values.overviewImage);
    data.append('facilityImage', values.facilityImage);
    data.append('tagImage', values.tagImage);
    for (let i = 0; i < values.attachmentDetails?.length; i++) {
      const file = values.attachmentDetails[i];
      if (file instanceof File) {
        data.append('attachments', file, encodeURIComponent(file.name));
      }
    }

    putFacility({
      data,
      headers: {
        'Content-Type': 'multipart/form-data; charset=UTF-8',
      },
    });
  });

  const { errors } = form.formState;
  const isOutdoorEquipment =
    form.watch('categoryId') === OUTDOOR_GYM_EQUIPMENT_ID;

  return (
    <Stack mx={responsiveMargin} spacing={5} mt={{ xs: 2, md: 10 }} mb={9}>
      {mdUp ? (
        <DesktopHeader
          postFacilityResult={putFacilityResult}
          onSubmit={onSubmit}
        />
      ) : (
        <MobileHeader
          postFacilityResult={putFacilityResult}
          onSubmit={onSubmit}
        />
      )}
      <Stack>
        <FormLabelText>
          구분 선택<span>*</span>
        </FormLabelText>
        <TypeSelect
          type={form.watch('categoryId')}
          onChange={(e: any) => form.setValue('categoryId', e.target.value)}
          helperText={_.get(errors, 'categoryId.message')}
        />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextInput
          form={form}
          isRequired={true}
          label="시설명"
          fieldPath="title"
          placeholder="예시 : 고려고등학교 야외운동기구"
        />
        <TextInput
          form={form}
          isRequired={true}
          label="관리번호"
          fieldPath="serialNumber"
          placeholder="예시 : 12345678"
        />
        <FormControl sx={{ width: '100%' }}>
          <FormLabelText>
            설치일<span>*</span>
          </FormLabelText>
          <BasicDatePicker
            date={form.watch('dateOfInstall')}
            setDate={(date: any) => form.setValue('dateOfInstall', date)}
          />
        </FormControl>
      </Stack>
      {isOutdoorEquipment ? (
        <>
          <Stack spacing={1}>
            <FormLabelText sx={{ mb: 0 }}>
              시설 / 기구명 선택<span>*</span>
            </FormLabelText>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1, md: 2 }}
            >
              <SearchAndSelect
                form={form}
                typeHelperText={_.get(errors, 'subCategoryType.message')}
                helperText={_.get(errors, 'subCategoryId.message')}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <MoreInformation />
              <InfoText>운동기구가 등록되어있지 않나요?</InfoText>
              <AddOutdoorEquipmentButton />
            </Stack>
          </Stack>
          <Stack>
            <FormLabelText>유형</FormLabelText>

            <Grid container>
              <Grid item xs={12} md={4} sx={{ mb: { xs: 2, md: 0 } }}>
                <RadioGroup
                  row
                  value={form.watch('ownershipType')}
                  onChange={e => {
                    form.setValue('ownershipType', e.target.value);
                  }}
                  sx={{ flexWrap: 'nowrap' }}
                >
                  <FormControlLabel
                    value={OwnershipTypeList.PUBLIC}
                    control={<Radio />}
                    label="공공용지"
                  />
                  <FormControlLabel
                    value={OwnershipTypeList.PRIVATE}
                    control={<Radio />}
                    label="사유지"
                  />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} md={8}>
                <UnLabelTextInput
                  form={form}
                  isRequired={false}
                  label=""
                  fieldPath="usedAs"
                  placeholder="예시 : 학교"
                />
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={5}>
            <TextInput
              form={form}
              isRequired={false}
              label="설치면적"
              fieldPath="occupyingSpace"
              placeholder="예시 : 1920"
              type="number"
            />
            <Stack>
              <FormLabelText>사업비(국비/시비/구비)</FormLabelText>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <UnLabelTextInput
                  form={form}
                  fieldPath="installCostNation"
                  placeholder="예시 :국비"
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">천원</InputAdornment>
                    ),
                  }}
                />
                <UnLabelTextInput
                  form={form}
                  fieldPath="installCostCity"
                  placeholder="예시 : 시비"
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">천원</InputAdornment>
                    ),
                  }}
                />
                <UnLabelTextInput
                  form={form}
                  fieldPath="installCostDistrict"
                  placeholder="예시 : 구비"
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">천원</InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Stack>
            <FormDivider />
          </Stack>
        </>
      ) : (
        <></>
      )}
      <Stack spacing={5}>
        <AddressForm form={form} />
        <TextInput
          form={form}
          isRequired={false}
          label="위치참고"
          fieldPath="addressNote"
          placeholder="예시 : 첨단체육공원, 공원입구 앞"
        />
        {isOutdoorEquipment ? (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextInput
                form={form}
                isRequired={false}
                label="해당동"
                fieldPath="actualRegion"
                placeholder="예시 : 광주 북구 중흥동"
              />
              <TextInput
                form={form}
                isRequired={false}
                label="행정동"
                fieldPath="administrativeRegion"
                placeholder="예시 : 광주 북구 중흥동"
              />
            </Stack>
            <FormDivider />
          </>
        ) : (
          <></>
        )}
      </Stack>
      <Stack spacing={5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl sx={{ width: '100%' }}>
            <FormLabelText>
              관리부서<span>*</span>
            </FormLabelText>
            <DepartmentSelect
              department={form.watch('maintenanceDepartment')}
              onChange={(e: any) =>
                form.setValue('maintenanceDepartment', e.target.value)
              }
              helperText={_.get(errors, 'maintenanceDepartment.message')}
            />
          </FormControl>
          <TextInput
            form={form}
            isRequired={false}
            label="연락처"
            fieldPath="contactNumber"
            placeholder="예시 : 01012341234"
          />
        </Stack>
        {isOutdoorEquipment ? (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl sx={{ width: '100%' }}>
                <FormLabelText>설치 / 담당부서</FormLabelText>
                <DepartmentSelect
                  department={form.watch('installDepartment')}
                  onChange={(e: any) =>
                    form.setValue('installDepartment', e.target.value)
                  }
                />
              </FormControl>
              <FormControl sx={{ width: '100%' }}>
                <FormLabelText>총괄부서</FormLabelText>
                <DepartmentSelect
                  department={form.watch('manageDepartment')}
                  onChange={(e: any) =>
                    form.setValue('manageDepartment', e.target.value)
                  }
                />
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl sx={{ width: '100%' }}>
                <FormLabelText>위탁기관</FormLabelText>
                <DepartmentSelect
                  department={form.watch('consignedOrg')}
                  onChange={(e: any) =>
                    form.setValue('consignedOrg', e.target.value)
                  }
                />
              </FormControl>
              <Stack direction="row" width="100%" spacing={1}>
                <FormControl sx={{ width: '100%' }}>
                  <FormLabelText>위탁기간</FormLabelText>
                  <BasicDatePicker
                    date={form.watch('consignedStartDate')}
                    setDate={(date: any) =>
                      form.setValue('consignedStartDate', date)
                    }
                  />
                </FormControl>
                <FormControl sx={{ width: '100%' }}>
                  <Box height="30px" />
                  <BasicDatePicker
                    date={form.watch('consignedEndDate')}
                    setDate={(date: any) =>
                      form.setValue('consignedEndDate', date)
                    }
                  />
                </FormControl>
              </Stack>
            </Stack>
          </>
        ) : (
          <></>
        )}
        <FormDivider />
      </Stack>
      <Stack spacing={4}>
        <FormTitle>시설물 점검</FormTitle>
        <Stack spacing={1}>
          <Typography>[현장사진]전경</Typography>
          <ImageInput
            form={form}
            fieldPath="overviewImage"
            fieldUrl="overviewImageUrl"
            fieldName="overview"
          />
        </Stack>
        <Stack spacing={1}>
          <Typography>[현장사진]시설물</Typography>
          <ImageInput
            form={form}
            fieldPath="facilityImage"
            fieldUrl="facilityImageUrl"
            fieldName="facility"
          />
        </Stack>
        <Stack spacing={1}>
          <Typography>[현장사진]표찰</Typography>
          <ImageInput
            form={form}
            fieldPath="tagImage"
            fieldUrl="tagImageUrl"
            fieldName="tag"
          />
        </Stack>
        <Stack>
          <TextAreaInput
            form={form}
            isRequired={false}
            label="비고"
            fieldPath="notes"
            placeholder="예시 : 비고 추가 작성"
          />
        </Stack>
        <Stack>
          <FormControl>
            <FileInput form={form} />
          </FormControl>
        </Stack>
      </Stack>
    </Stack>
  );
}

function DesktopHeader({ onSubmit, postFacilityResult }: any) {
  const navigate = useNavigate();
  return (
    <>
      <IconButton
        sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
        onClick={() => navigate('/map')}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormTitle>시설물 정보 수정</FormTitle>
        <Box />
        <Stack direction="row" spacing={1}>
          <PrimaryOutlinedButton
            sx={{ width: 130, height: 40 }}
            onClick={() => navigate('/map')}
          >
            취소
          </PrimaryOutlinedButton>
          <PrimaryButton sx={{ width: 130, height: 40 }} onClick={onSubmit}>
            저장
          </PrimaryButton>
          <ApiResultModal
            apiResult={postFacilityResult}
            successTitle="수정됐습니다."
            successCode={200}
            onSuccessClose={() => {
              navigate(`/map`);
            }}
            failTitle="관리자에게 문의해주세요."
            ErrorCodeTable={{
              PAYLOAD_INVALID: { title: '입력값을 확인해주세요' },
              DUPLICATE_SERIAL_NUMBER: {
                title: '관리번호 중복',
                content: '이미 존재하는 관리번호입니다.',
              },
              SERIAL_NUMBER_DOES_NOT_MATCH: {
                title: '시설명 확인',
                content: `해당 관리번호로 등록된 시설명이 존재합니다.\n 등록된 시설명을 확인해 주세요.`,
              },
            }}
          />
        </Stack>
      </Stack>
      <ScrollUpButton />
      <Box sx={{ mt: '24px !important' }}>
        <FormDivider />
      </Box>
    </>
  );
}

function AddressForm({ form }) {
  const { setRef, map } = useNaverMap({
    draggable: false,
    pinchZoom: false,
    scrollWheel: false,
    keyboardShortcuts: false,
    disableDoubleTapZoom: true,
    disableDoubleClickZoom: true,
    disableTwoFingerTapZoom: true,
  });
  const { lat, lng, jibunAddress, roadAddress, zoom } = useMapSearchParams();

  useEffect(() => {
    if (map) {
      const position = new window.naver.maps.LatLng(lat, lng);
      new window.naver.maps.Marker({
        position,
        map,
        icon: {
          content: `<div>
          <img style="width:36px;height:36px;" src="${markerImg}"/>
        </div>
        `,
          size: new window.naver.maps.Size(36, 36),
        },
      });
    }
  }, [map, lat, lng]);
  const navigate = useNavigate();
  return (
    <Stack>
      <FormLabelText>
        주소<span>*</span>
      </FormLabelText>
      <Typography>{jibunAddress || '-'}</Typography>
      <Box
        sx={{ width: '100%', height: { xs: 150, md: 300 } }}
        ref={setRef}
        onClick={() => {
          navigate(
            `/address/${form.watch(
              'facilityId',
            )}?lat=${lat}&lng=${lng}&zoom=${zoom}&roadAddress=${roadAddress}&jibunAddress=${jibunAddress}`,
          );
        }}
      ></Box>
    </Stack>
  );
}

function MobileHeader({ onSubmit, postFacilityResult }: any) {
  const navigate = useNavigate();

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <IconButton
        sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
        onClick={() => navigate('/map')}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <MobileTitle>시설물 정보 수정</MobileTitle>
      <Button variant="text" onClick={onSubmit}>
        저장
      </Button>
      <ApiResultModal
        apiResult={postFacilityResult}
        successTitle="수정됐습니다."
        successCode={200}
        onSuccessClose={() => {
          navigate(`/map`);
        }}
        failTitle="관리자에게 문의해주세요."
        ErrorCodeTable={{
          PAYLOAD_INVALID: { title: '입력값을 확인해주세요' },
          DUPLICATE_SERIAL_NUMBER: {
            title: '관리번호 중복',
            content: '이미 존재하는 관리번호입니다.',
          },
          SERIAL_NUMBER_DOES_NOT_MATCH: {
            title: '시설명 확인',
            content: `해당 관리번호로 등록된 시설명이 존재합니다.\n 등록된 시설명을 확인해 주세요.`,
          },
        }}
      />
      <ScrollUpButton />
    </Stack>
  );
}
